import type {MouseEvent, PointerEvent, ReactNode} from "react";
import {useContext, useEffect, useRef} from "react";
import {Text, UnstyledButton} from "@mantine/core";
import BuildingIcon from "../../../../../assets/building.svg?react";
import {GPTMetaContext} from "../MetaProvider";
import {useCitationPreview} from "../../../citation-preview/useCitationPreview";
import classes from "./CompanyReference.module.css";

type Props = {
    orgNr: string;
    name: ReactNode;
};

/** How long a hover must linger on the chip before the preview opens. Same
 *  delay as the citation marker. */
const HOVER_OPEN_DELAY_MS = 250;

/** True on a device with a real pointer that can hover. False on touch-only. */
function supportsHover(): boolean {
    return (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(hover: hover)").matches
    );
}

/** An inline `[Name](company:<orgNr>)` reference. Renders as a chip with a
 *  building icon, and opens the company card on hover or click. Without a
 *  card for that orgNr it renders the plain name, no icon, no popover. */
export const CompanyReference = ({orgNr, name}: Props) => {
    const meta = useContext(GPTMetaContext);
    const {state, openCompanyPreview, scheduleClose, cancelScheduledClose} =
        useCitationPreview();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const card = meta?.proffCompanies?.[orgNr];

    const clearHoverTimer = () => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    };

    // Clear a pending hover timer on unmount so it never fires after removal.
    useEffect(() => clearHoverTimer, []);

    if (!card) {
        return <span>{name}</span>;
    }

    const isOpen = state?.kind === "company" && state.anchorEl === anchorRef.current;

    const handleClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        clearHoverTimer();
        openCompanyPreview(card, anchorRef.current, "click");
    };

    const handlePointerEnter = (event: PointerEvent) => {
        if (event.pointerType === "touch" || !supportsHover()) return;

        if (isOpen && state?.openedBy === "hover") {
            cancelScheduledClose();
            return;
        }

        hoverTimerRef.current = setTimeout(() => {
            hoverTimerRef.current = null;
            openCompanyPreview(card, anchorRef.current, "hover");
        }, HOVER_OPEN_DELAY_MS);
    };

    const handlePointerLeave = () => {
        clearHoverTimer();
        if (isOpen && state?.openedBy === "hover") {
            scheduleClose();
        }
    };

    // A keyboard user tabbing onto the chip gets the same delayed open as a
    // hovering pointer, so the card is reachable without a mouse.
    const handleFocus = () => {
        if (isOpen) {
            if (state?.openedBy === "hover") cancelScheduledClose();
            return;
        }

        hoverTimerRef.current = setTimeout(() => {
            hoverTimerRef.current = null;
            openCompanyPreview(card, anchorRef.current, "hover");
        }, HOVER_OPEN_DELAY_MS);
    };

    const handleBlur = () => {
        clearHoverTimer();
        if (isOpen && state?.openedBy === "hover") {
            scheduleClose();
        }
    };

    return (
        <UnstyledButton
            ref={anchorRef}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            className={classes.chip}
            onClick={handleClick}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            onFocus={handleFocus}
            onBlur={handleBlur}
        >
            <BuildingIcon className={classes.icon} />
            <Text span inherit className={classes.name}>
                {name}
            </Text>
        </UnstyledButton>
    );
};
